import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
// import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import LogoAnLangOnlyLayout from '../layouts/LogoAnLangOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import AppGuard from '../guards/AppGuard';
// components
import LoadingLogoScreen from '../components-custom/LoadingLogoScreen';
//
import { PATH } from './paths';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingLogoScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Dashboard Routes
    {
      path: '/',
      element: (
        <AuthGuard>
          <AppGuard>
            <DashboardLayout />
          </AppGuard>
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH.overview} replace />, index: true },
        { path: 'overview', element: <Overview /> },
        {
          path: 'invoice',
          children: [
            { element: <Navigate to={PATH.invoice.sale.root} replace />, index: true },
            { path: 'sale', element: <Invoice type="sale" /> },
            { path: 'sale/new', element: <InvoiceNew type="sale" /> },
            { path: 'sale/:id', element: <InvoiceDetail type="sale" /> },
            { path: 'sale/:id/edit', element: <InvoiceNew type="sale" isEdit /> },
            { path: 'sale/:id/duplicate', element: <InvoiceNew type="sale" isDuplicate /> },

            { path: 'purchase', element: <Invoice type="purchase" /> },
            { path: 'purchase/new', element: <InvoiceNew type="purchase" /> },
            { path: 'purchase/:id', element: <InvoiceDetail type="purchase" /> },
            { path: 'purchase/:id/edit', element: <InvoiceNew type="purchase" isEdit /> },
            { path: 'purchase/:id/duplicate', element: <InvoiceNew type="purchase" isDuplicate /> },

            { path: 'note', element: <Invoice type="note" /> },
            // { path: 'note/new', element: <InvoiceNew type="note" /> },
            { path: 'note/:id', element: <InvoiceDetail type="note" /> },
            { path: 'note/:id/edit', element: <InvoiceNew type="note" isEdit /> },
            { path: 'note/:id/duplicate', element: <InvoiceNew type="note" isDuplicate /> },

            { path: 'prepayment', element: <Invoice type="prepayment" /> },
            { path: 'prepayment/new', element: <InvoicePrepaymentNew /> },
            { path: 'prepayment/:id', element: <InvoiceDetail type="prepayment" /> },
            { path: 'prepayment/:id/edit', element: <InvoicePrepaymentNew mode="edit" /> },
            { path: 'prepayment/:id/duplicate', element: <InvoicePrepaymentNew mode="duplicate" /> },

            { path: 'expense', element: <Expense /> },
            { path: 'expense/new', element: <ExpenseNew /> },
            { path: 'expense/:id', element: <ExpenseDetail /> },
            { path: 'expense/:id/edit', element: <ExpenseNew isEdit /> },

            { path: 'payment', element: <Payment /> },
            { path: 'payment/:id', element: <PaymentDetail /> },
          ],
        },
        {
          path: 'accounting',
          children: [
            { element: <Navigate to={PATH.accounting.account.root} replace />, index: true },
            { path: 'account', element: <Account /> },
            { path: 'account/new', element: <AccountNew /> },
            { path: 'account/:id', element: <AccountDetail /> },
            { path: 'account/:id/edit', element: <AccountNew isEdit /> },

            { path: 'trialBalance', element: <TrialBalance /> },
            { path: 'ProfitLoss', element: <ProfitLoss /> },
            { path: 'ProfitLossDetailed', element: <ProfitLossDetailed /> },
            { path: 'BalanceSheet', element: <BalanceSheet /> },
            // { path: 'BalanceSheetDetailed', element: <BalanceSheetDetailed /> },

            { path: 'journalEntry', element: <JournalEntry /> },
            { path: 'journalEntry/new', element: <JournalEntryNew /> },
            { path: 'journalEntry/:id', element: <JournalEntryDetail /> },
            { path: 'journalEntry/:id/edit', element: <JournalEntryNew isEdit /> },

            { path: 'accountSettings', element: <AccountSettings /> },
          ],
        },
        {
          path: 'stock',
          children: [
            { element: <Navigate to={PATH.stock.transfer.root} replace />, index: true },
            { path: 'transfer', element: <StockTransfer /> },
            { path: 'transfer/new', element: <StockTransferNew /> },
            { path: 'transfer/:id', element: <StockTransferDetail /> },
            { path: 'transfer/:id/edit', element: <StockTransferNew isEdit /> },

            { path: 'adjustment', element: <StockAdjustment /> },
            { path: 'adjustment/new', element: <StockAdjustmentNew /> },
            { path: 'adjustment/:id', element: <StockAdjustmentDetail /> },
            { path: 'adjustment/:id/edit', element: <StockAdjustmentNew isEdit /> },

            { path: 'transaction', element: <StockTransaction /> },
          ],
        },
        {
          path: 'customer',
          children: [
            { element: <Partner type="customer" />, index: true },
            { path: ':id', element: <PartnerDetail type="customer" /> },
          ],
        },
        {
          path: 'supplier',
          children: [
            { element: <Partner type="supplier" />, index: true },
            { path: ':id', element: <PartnerDetail type="supplier" /> },
          ],
        },
        {
          path: 'product',
          children: [
            { element: <Product />, index: true },
            { path: 'new', element: <ProductNew /> },
            { path: ':id', element: <ProductDetail /> },
            { path: ':id/edit', element: <ProductNew isEdit /> },
          ],
        },
        {
          path: 'category-product',
          children: [
            { element: <CategoryProduct />, index: true },
            { path: ':id', element: <CategoryProductDetail /> },
          ],
        },
        {
          path: 'report',
          children: [
            { element: <Navigate to={PATH.report.sale} replace />, index: true },
            { path: 'sale', element: <Report type="sale" /> },
            { path: 'purchase', element: <Report type="purchase" /> },
            { path: 'expense', element: <Report type="expense" /> },
            { path: 'tax', element: <ReportTax /> },
            { path: 'stock', element: <ReportStock /> },
            { path: 'profit-loss', element: <ReportProfitLoss /> },
            { path: 'dispatch', element: <ReportDispatch /> },
          ],
        },
        {
          path: 'project',
          children: [
            { element: <Navigate to={PATH.project.root} replace />, index: true },
            { path: 'board', element: <ProjectBoard /> },
          ],
        },
        {
          path: 'management',
          children: [
            { element: <Navigate to={PATH.management.settings.root} replace />, index: true },
            {
              path: 'member',
              children: [
                { element: <Member />, index: true },
                { path: ':id', element: <MemberDetail /> },
              ],
            },
            {
              path: 'branch',
              children: [
                { element: <Branch />, index: true },
                { path: ':id', element: <BranchDetail /> },
              ],
            },
            {
              path: 'team',
              children: [
                { element: <Team />, index: true },
                { path: ':id', element: <TeamDetail /> },
              ],
            },
            {
              path: 'payment-method',
              children: [
                { element: <PaymentMethod />, index: true },
                { path: ':id', element: <PaymentMethodDetail /> },
              ],
            },
            { path: 'history-log', element: <HistoryLog /> },
          ],
        },
        {
          path: 'hr',
          children: [
            { element: <Navigate to={PATH.hr.dashboard} replace />, index: true },
            { path: 'dashboard', element: <HrDashboard /> },
            {
              path: 'holiday',
              children: [
                { element: <HrHoliday />, index: true },
                { path: 'new', element: <HrHolidayNew /> },
                { path: ':id', element: <HrHolidayDetail /> },
                { path: ':id/edit', element: <HrHolidayNew isEdit /> },
              ],
            },
            {
              path: 'department',
              children: [
                { element: <HrDepartment />, index: true },
                { path: 'new', element: <HrDepartmentNew /> },
                { path: ':id', element: <HrDepartmentDetail /> },
                { path: ':id/edit', element: <HrDepartmentNew isEdit /> },
              ],
            },
            {
              path: 'employee',
              children: [
                { element: <HrEmployee />, index: true },
                { path: 'new', element: <HrEmployeeNew /> },
                { path: ':id', element: <HrEmployeeDetail /> },
                { path: ':id/edit', element: <HrEmployeeNew isEdit /> },
              ],
            },
            {
              path: 'vacation',
              children: [
                { element: <HrVacation />, index: true },
                { path: 'new', element: <HrVacationNew /> },
                { path: ':id', element: <HrVacationDetail /> },
                { path: ':id/edit', element: <HrVacationNew isEdit /> },
              ],
            },
            {
              path: 'vacation-type',
              children: [
                { element: <HrVacationType />, index: true },
                { path: 'new', element: <HrVacationTypeNew /> },
                { path: ':id', element: <HrVacationTypeDetail /> },
                { path: ':id/edit', element: <HrVacationTypeNew isEdit /> },
              ],
            },
            {
              path: 'vacation-policy',
              children: [
                { element: <HrVacationPolicy />, index: true },
                { path: 'new', element: <HrVacationPolicyNew /> },
                { path: ':id', element: <HrVacationPolicyDetail /> },
                { path: ':id/edit', element: <HrVacationPolicyNew isEdit /> },
              ],
            },
            {
              path: 'payroll',
              children: [
                { element: <HrPayroll />, index: true },
                { path: 'new', element: <HrPayrollNew /> },
                { path: ':id', element: <HrPayrollDetail /> },
                { path: ':id/edit', element: <HrPayrollNew isEdit /> },
              ],
            },
            {
              path: 'attendance',
              children: [
                { element: <HrAttendance />, index: true },
                { path: 'bulk', element: <HrAttendanceBulk /> },
                { path: 'new', element: <HrAttendanceNew /> },
                { path: 'settings', element: <HrAttendanceSettings /> },
                { path: 'daily', element: <HrAttendanceDaily /> },
                { path: 'monthly', element: <HrAttendanceMonthly /> },
                { path: ':id', element: <HrAttendanceDetail /> },
                { path: ':id/edit', element: <HrAttendanceNew isEdit /> },
              ],
            },
          ],
        },
      ],
    },

    // Employee Portal Routes
    {
      path: 'portal',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH.portal.overview} replace />, index: true },
        { path: 'overview', element: <PortalOverview /> },
        { path: 'profile', element: <MyProfile /> },
        {
          path: 'vacations',
          children: [
            { element: <MyVacations />, index: true },
            { path: 'request', element: <MyVacationRequest /> },
            { path: ':id', element: <MyVacationDetail /> },
            { path: ':id/edit', element: <MyVacationRequest /> },
          ],
        },
        {
          path: 'attendance',
          children: [
            { element: <MyAttendance />, index: true },
            { path: ':id', element: <MyAttendanceDetail /> },
          ],
        },
      ],
    },

    // Settings Routes
    {
      path: 'management',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH.management.settings.root} replace />, index: true },
        { path: 'settings', element: <Settings /> },
        { path: 'subscription', element: <Subscription /> },
        { path: 'subscription/suspend', element: <BusinessSuspend /> },
        { path: 'subscription/new', element: <Subscription isNew /> },
        { path: 'subscription/success', element: <SubscriptionSuccess /> },
        { path: 'subscription/invoice/:id', element: <SubscriptionInvoiceDetail /> },
      ],
    },

    // Auth Routes
    {
      path: '/',
      element: (
        <GuestGuard>
          <LogoOnlyLayout />
        </GuestGuard>
      ),
      children: [
        { path: 'login', element: <Login /> },
        { path: 'forgot-password', element: <ForgotPassword /> },
        { path: 'reset-password/:code', element: <ResetPassword /> },
        { path: 'set-password/:code', element: <SetPassword /> },
        { path: 'register', element: <Register /> },
        { path: 'sso', element: <SSO /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoAnLangOnlyLayout />,
      children: [
        { path: 'invitation/:code', element: <Invitation /> },
        { path: 'activation', element: <Activation /> },
        { path: 'activation/:code', element: <Activation /> },
        { path: 'suspend', element: <Suspend /> },
        { path: '403', element: <Page403 /> },
        { path: '404', element: <Page404 /> },
        { path: 'upgrade', element: <Upgrade /> },
        { path: 'payment-card', element: <PaymentCard /> },
        { path: '*', element: <Navigate to={PATH.misc.page404} replace /> },
      ],
    },
    { path: '*', element: <Navigate to={PATH.misc.page404} replace /> },
  ]);
}

// IMPORT COMPONENTS

// Dashboard
const Overview = Loadable(lazy(() => import('../pages/overview/Overview')));

const Invoice = Loadable(lazy(() => import('../pages/invoice/Invoice')));
const InvoiceDetail = Loadable(lazy(() => import('../pages/invoice/InvoiceDetail')));
const InvoiceNew = Loadable(lazy(() => import('../pages/invoice/InvoiceNew')));
const InvoicePrepaymentNew = Loadable(lazy(() => import('../pages/invoice/InvoicePrepaymentNew')));

const Expense = Loadable(lazy(() => import('../pages/expense/Expense')));
const ExpenseDetail = Loadable(lazy(() => import('../pages/expense/ExpenseDetail')));
const ExpenseNew = Loadable(lazy(() => import('../pages/expense/ExpenseNew')));

const Payment = Loadable(lazy(() => import('../pages/payment/Payment')));
const PaymentDetail = Loadable(lazy(() => import('../pages/payment/PaymentDetail')));

const Account = Loadable(lazy(() => import('../pages/accounting/account/Account')));
const AccountDetail = Loadable(lazy(() => import('../pages/accounting/account/AccountDetail')));
const AccountNew = Loadable(lazy(() => import('../pages/accounting/account/AccountNew')));
const TrialBalance = Loadable(lazy(() => import('../pages/accounting/reports/TrialBalanceReport')));
const ProfitLoss = Loadable(lazy(() => import('../pages/accounting/reports/ProfitLoss')));
const ProfitLossDetailed = Loadable(lazy(() => import('../pages/accounting/reports/ProfitLossDetailed')));
const BalanceSheet = Loadable(lazy(() => import('../pages/accounting/reports/BalanceSheet')));
// const BalanceSheetDetailed = Loadable(lazy(() => import('../pages/accounting/reports/BalanceSheetDetailed')));
const JournalEntry = Loadable(lazy(() => import('../pages/accounting/journalEntry/JournalEntry')));
const JournalEntryDetail = Loadable(lazy(() => import('../pages/accounting/journalEntry/JournalEntryDetail')));
const JournalEntryNew = Loadable(lazy(() => import('../pages/accounting/journalEntry/JournalEntryNew')));
const AccountSettings = Loadable(lazy(() => import('../pages/accounting/management/AccountSettings')));

const StockTransfer = Loadable(lazy(() => import('../pages/stock/transfer/StockTransfer')));
const StockTransferDetail = Loadable(lazy(() => import('../pages/stock/transfer/StockTransferDetail')));
const StockTransferNew = Loadable(lazy(() => import('../pages/stock/transfer/StockTransferNew')));

const StockAdjustment = Loadable(lazy(() => import('../pages/stock/adjustment/StockAdjustment')));
const StockAdjustmentDetail = Loadable(lazy(() => import('../pages/stock/adjustment/StockAdjustmentDetail')));
const StockAdjustmentNew = Loadable(lazy(() => import('../pages/stock/adjustment/StockAdjustmentNew')));

const StockTransaction = Loadable(lazy(() => import('../pages/stock/transaction/StockTransaction')));

const Partner = Loadable(lazy(() => import('../pages/master/partner/Partner')));
const PartnerDetail = Loadable(lazy(() => import('../pages/master/partner/PartnerDetail')));

const Product = Loadable(lazy(() => import('../pages/master/product/Product')));
const ProductDetail = Loadable(lazy(() => import('../pages/master/product/ProductDetail')));
const ProductNew = Loadable(lazy(() => import('../pages/master/product/ProductNew')));

const CategoryProduct = Loadable(lazy(() => import('../pages/master/categoryProduct/CategoryProduct')));
const CategoryProductDetail = Loadable(lazy(() => import('../pages/master/categoryProduct/CategoryProductDetail')));

const Report = Loadable(lazy(() => import('../pages/report/Report')));
const ReportStock = Loadable(lazy(() => import('../pages/report/ReportStock')));
const ReportTax = Loadable(lazy(() => import('../pages/report/ReportTax')));
const ReportProfitLoss = Loadable(lazy(() => import('../pages/report/ReportProfitLoss')));
const ReportDispatch = Loadable(lazy(() => import('../pages/report/ReportDispatch')));

const ProjectBoard = Loadable(lazy(() => import('../pages/project/ProjectBoard')));

const Settings = Loadable(lazy(() => import('../pages/management/settings/Settings')));
const Subscription = Loadable(lazy(() => import('../pages/management/subscription/Subscription')));
const SubscriptionSuccess = Loadable(lazy(() => import('../pages/management/subscription/SubscriptionSuccess')));
const SubscriptionInvoiceDetail = Loadable(
  lazy(() => import('../pages/management/subscription/SubscriptionInvoiceDetail'))
);
const Member = Loadable(lazy(() => import('../pages/management/member/Member')));
const MemberDetail = Loadable(lazy(() => import('../pages/management/member/MemberDetail')));
const Branch = Loadable(lazy(() => import('../pages/management/branch/Branch')));
const BranchDetail = Loadable(lazy(() => import('../pages/management/branch/BranchDetail')));
const Team = Loadable(lazy(() => import('../pages/management/team/Team')));
const TeamDetail = Loadable(lazy(() => import('../pages/management/team/TeamDetail')));
const PaymentMethod = Loadable(lazy(() => import('../pages/management/paymentMethod/PaymentMethod')));
const PaymentMethodDetail = Loadable(lazy(() => import('../pages/management/paymentMethod/PaymentMethodDetail')));
const HistoryLog = Loadable(lazy(() => import('../pages/management/historyLog/HistoryLog')));

const HrDashboard = Loadable(lazy(() => import('../pages/hr/HrDashboard')));
const HrHoliday = Loadable(lazy(() => import('../pages/hr/holiday/HrHoliday')));
const HrHolidayDetail = Loadable(lazy(() => import('../pages/hr/holiday/HrHolidayDetail')));
const HrHolidayNew = Loadable(lazy(() => import('../pages/hr/holiday/HrHolidayNew')));

const HrDepartment = Loadable(lazy(() => import('../pages/hr/department/HrDepartment')));
const HrDepartmentDetail = Loadable(lazy(() => import('../pages/hr/department/HrDepartmentDetail')));
const HrDepartmentNew = Loadable(lazy(() => import('../pages/hr/department/HrDepartmentNew')));

const HrEmployee = Loadable(lazy(() => import('../pages/hr/employee/HrEmployee')));
const HrEmployeeDetail = Loadable(lazy(() => import('../pages/hr/employee/HrEmployeeDetail')));
const HrEmployeeNew = Loadable(lazy(() => import('../pages/hr/employee/HrEmployeeNew')));

const HrVacation = Loadable(lazy(() => import('../pages/hr/vacation/HrVacation')));
const HrVacationDetail = Loadable(lazy(() => import('../pages/hr/vacation/HrVacationDetail')));
const HrVacationNew = Loadable(lazy(() => import('../pages/hr/vacation/HrVacationNew')));

const HrVacationType = Loadable(lazy(() => import('../pages/hr/vacation-type/HrVacationType')));
const HrVacationTypeDetail = Loadable(lazy(() => import('../pages/hr/vacation-type/HrVacationTypeDetail')));
const HrVacationTypeNew = Loadable(lazy(() => import('../pages/hr/vacation-type/HrVacationTypeNew')));

const HrVacationPolicy = Loadable(lazy(() => import('../pages/hr/vacation-policy/HrVacationPolicy')));
const HrVacationPolicyDetail = Loadable(lazy(() => import('../pages/hr/vacation-policy/HrVacationPolicyDetail')));
const HrVacationPolicyNew = Loadable(lazy(() => import('../pages/hr/vacation-policy/HrVacationPolicyNew')));

const HrPayroll = Loadable(lazy(() => import('../pages/hr/payroll/HrPayroll')));
const HrPayrollDetail = Loadable(lazy(() => import('../pages/hr/payroll/HrPayrollDetail')));
const HrPayrollNew = Loadable(lazy(() => import('../pages/hr/payroll/HrPayrollNew')));

const HrAttendance = Loadable(lazy(() => import('../pages/hr/attendance/HrAttendance')));
const HrAttendanceBulk = Loadable(lazy(() => import('../pages/hr/attendance/HrAttendanceBulk')));
const HrAttendanceNew = Loadable(lazy(() => import('../pages/hr/attendance/HrAttendanceNew')));
const HrAttendanceDetail = Loadable(lazy(() => import('../pages/hr/attendance/HrAttendanceDetail')));
const HrAttendanceSettings = Loadable(lazy(() => import('../pages/hr/attendance/HrAttendanceSettings')));
const HrAttendanceDaily = Loadable(lazy(() => import('../pages/hr/attendance/HrAttendanceDaily')));
const HrAttendanceMonthly = Loadable(lazy(() => import('../pages/hr/attendance/HrAttendanceMonthly')));

// Employee Portal
const PortalOverview = Loadable(lazy(() => import('../pages/hr/portal/PortalOverview')));
const MyProfile = Loadable(lazy(() => import('../pages/hr/portal/MyProfile')));
const MyVacations = Loadable(lazy(() => import('../pages/hr/portal/MyVacations')));
const MyVacationDetail = Loadable(lazy(() => import('../pages/hr/portal/MyVacationDetail')));
const MyVacationRequest = Loadable(lazy(() => import('../pages/hr/portal/MyVacationRequest')));
const MyAttendance = Loadable(lazy(() => import('../pages/hr/portal/MyAttendance')));
const MyAttendanceDetail = Loadable(lazy(() => import('../pages/hr/portal/MyAttendanceDetail')));

// Authentication
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const ForgotPassword = Loadable(lazy(() => import('../pages/auth/ForgotPassword')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const SetPassword = Loadable(lazy(() => import('../pages/auth/SetPassword')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const Activation = Loadable(lazy(() => import('../pages/auth/Activation')));
const SSO = Loadable(lazy(() => import('../pages/auth/SSO')));

const Invitation = Loadable(lazy(() => import('../pages/auth/Invitation')));

const PaymentCard = Loadable(lazy(() => import('../pages/auth/PaymentCard')));

const Page403 = Loadable(lazy(() => import('../pages/misc/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/misc/Page404')));
const Suspend = Loadable(lazy(() => import('../pages/misc/Suspend')));
const BusinessSuspend = Loadable(lazy(() => import('../pages/misc/BusinessSuspend')));
const Upgrade = Loadable(lazy(() => import('../pages/misc/Upgrade')));
